import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { GitBranch, Bot, Database, History, ArrowLeft, User } from 'lucide-react';
import { apiService } from '../services/api';
import {
  SharedProjectDetail,
  SharedWorkflowSummary,
  SharedAgentSummary,
  SharedModelSummary,
  SharedProjectCard,
  PublishedVersionSummary,
} from '../types';
import SharedProjectBanner from '../components/social/SharedProjectBanner';
import FollowButton from '../components/social/FollowButton';
import ProjectCard from '../components/social/ProjectCard';
import VersionSelector from '../components/social/VersionSelector';
import { useSocialStore } from '../store/socialStore';
import { useAuthStore } from '../store';
import { useContextualNavigation } from '../hooks/useContextualNavigation';

const SharedProjectViewPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { navigateWithContext, getBackNavigation } = useContextualNavigation();
  const [searchParams] = useSearchParams();
  const backNav = getBackNavigation('/explore', 'Back to Explore');

  // Social store for follow state
  const { viewedProfile, loadUserProfile, followUser, unfollowUser } = useSocialStore();
  const { user } = useAuthStore();

  const [project, setOperation] = useState<SharedProjectDetail | null>(null);
  const [creatorOperations, setCreatorOperations] = useState<SharedProjectCard[]>([]);
  const [followLoading, setFollowLoading] = useState(false);
  const [workflows, setWorkflows] = useState<SharedWorkflowSummary[]>([]);
  const [agents, setAgents] = useState<SharedAgentSummary[]>([]);
  const [models, setModels] = useState<SharedModelSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [versionLoading, setVersionLoading] = useState(false);
  const [versions, setVersions] = useState<PublishedVersionSummary[]>([]);
  const [selectedVersionId, setSelectedVersionId] = useState<number | null>(null);
  const [selectedVersionName, setSelectedVersionName] = useState<string | null>(null);

  // Store live data so we can revert when switching back to "Latest"
  const [liveWorkflows, setLiveWorkflows] = useState<SharedWorkflowSummary[]>([]);
  const [liveAgents, setLiveAgents] = useState<SharedAgentSummary[]>([]);
  const [liveModels, setLiveModels] = useState<SharedModelSummary[]>([]);

  const numericOperationId = projectId ? parseInt(projectId, 10) : 0;

  // Read ?version= query param on mount
  useEffect(() => {
    const versionParam = searchParams.get('version');
    if (versionParam) {
      const versionId = parseInt(versionParam, 10);
      if (!isNaN(versionId)) {
        setSelectedVersionId(versionId);
      }
    }
  }, [searchParams]);

  useEffect(() => {
    if (numericOperationId) {
      loadOperation();
    }
  }, [numericOperationId]);

  // Load version snapshot when selectedVersionId changes
  useEffect(() => {
    if (!numericOperationId) return;

    if (selectedVersionId === null) {
      // Revert to live data
      setWorkflows(liveWorkflows);
      setAgents(liveAgents);
      setModels(liveModels);
      setSelectedVersionName(null);
      return;
    }

    const loadVersion = async () => {
      setVersionLoading(true);
      try {
        const versionDetail = await apiService.getSharedVersionDetail(numericOperationId, selectedVersionId);
        setSelectedVersionName(versionDetail.version_name);

        // Map snapshot types to display types
        setWorkflows(
          versionDetail.snapshot.orchestrations.map((o) => ({
            orchestration_id: o.orchestration_id,
            name: o.name,
            description: o.description,
            step_count: o.steps.length,
            created_at: null,
          }))
        );
        setAgents(
          versionDetail.snapshot.agents.map((a) => ({
            agent_id: a.agent_id,
            name: a.name,
            instructions: a.instructions,
            handoff_description: a.handoff_description,
            model: a.model,
            model_settings: a.model_settings,
            output_type: a.output_type,
            tools: a.tools,
            created_at: null,
          }))
        );
        setModels(
          versionDetail.snapshot.models.map((m) => ({
            model_id: m.model_id,
            class_name: m.class_name,
            description: m.description,
            properties: m.properties,
            relations: null,
            created_at: null,
          }))
        );
      } catch (error) {
        console.error('Failed to load version:', error);
      } finally {
        setVersionLoading(false);
      }
    };

    loadVersion();
  }, [selectedVersionId, numericOperationId]);

  const loadOperation = async () => {
    setLoading(true);
    try {
      const [projectData, orchData, agentData, modelData] = await Promise.all([
        apiService.getSharedProject(numericOperationId),
        apiService.getSharedProjectWorkflows(numericOperationId),
        apiService.getSharedProjectAgents(numericOperationId),
        apiService.getSharedProjectModels(numericOperationId),
      ]);
      setOperation(projectData);
      setWorkflows(orchData);
      setAgents(agentData);
      setModels(modelData);

      // Store live data for reverting
      setLiveWorkflows(orchData);
      setLiveAgents(agentData);
      setLiveModels(modelData);

      // Load published versions
      try {
        const versionData = await apiService.listSharedProjectVersions(numericOperationId);
        setVersions(versionData.versions);

        // If ?version= param was set before data loaded, trigger version load now
        const versionParam = searchParams.get('version');
        if (versionParam) {
          const versionId = parseInt(versionParam, 10);
          if (!isNaN(versionId)) {
            setSelectedVersionId(versionId);
          }
        }
      } catch {
        // Versions may not be available
      }

      // Record view
      apiService.recordProjectView(numericOperationId).catch(() => {});
    } catch (error) {
      console.error('Failed to load shared project:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load creator profile for follow state, and their other projects
  useEffect(() => {
    if (project?.owner_user_id) {
      loadUserProfile(project.owner_user_id);

      // Load other projects by the same creator (excluding the current project)
      apiService
        .getExploreProjects('recent', 0, 4, project.owner_user_id)
        .then((response) => {
          setCreatorOperations(
            response.projects.filter((p) => p.project_id !== numericOperationId).slice(0, 3)
          );
        })
        .catch((error) => {
          console.error('Failed to load creator projects:', error);
        });
    }
  }, [project?.owner_user_id, numericOperationId, loadUserProfile]);

  const handleFollowToggle = async () => {
    if (!project) return;
    setFollowLoading(true);
    try {
      if (viewedProfile?.is_following) {
        await unfollowUser(project.owner_user_id);
      } else {
        await followUser(project.owner_user_id);
      }
    } finally {
      setFollowLoading(false);
    }
  };

  const handleWorkflowClick = (workflowId: number) => {
    navigateWithContext(
      `/shared/projects/${numericOperationId}/workflows/${workflowId}`,
      project?.name || 'Project'
    );
  };

  const handleCreatorOperationClick = (projectId: number) => {
    navigateWithContext(`/shared/projects/${projectId}`, project?.name || 'Project');
  };

  // Don't show follow button if viewing your own project
  // Note: backend returns user_id (not id) so we access both for safety
  const currentUserId = (user as any)?.user_id ?? user?.id;
  const isOwnOperation = currentUserId && project ? String(currentUserId) === String(project.owner_user_id) : false;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-gray-500 dark:text-gray-400">Loading project...</p>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-gray-500 dark:text-gray-400">Project not found.</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Back Navigation */}
      <Link
        to={backNav.path}
        className="inline-flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-4 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" aria-hidden="true" />
        {backNav.label}
      </Link>

      {/* Banner with Follow Button */}
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <SharedProjectBanner
            ownerUsername={project.owner_username}
            ownerUserId={project.owner_user_id}
            viewCount={project.view_count}
            projectName={project.name}
          />
        </div>
        {!isOwnOperation && viewedProfile && (
          <FollowButton
            userId={project.owner_user_id}
            isFollowing={viewedProfile.is_following}
            onToggle={handleFollowToggle}
            loading={followLoading}
          />
        )}
      </div>

      {/* Project Header */}
      <div className="mt-6 mb-8">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{project.name}</h1>
            {project.description && (
              <p className="text-gray-600 dark:text-gray-400 mt-2">{project.description}</p>
            )}
          </div>
          {versions.length > 0 && (
            <VersionSelector
              projectId={numericOperationId}
              versions={versions}
              selectedVersionId={selectedVersionId}
              onVersionSelect={setSelectedVersionId}
            />
          )}
        </div>
      </div>

      {/* Version indicator */}
      {selectedVersionId !== null && selectedVersionName && (
        <div className="mb-6 flex items-center gap-2 px-4 py-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg text-sm text-blue-700 dark:text-blue-300">
          <History className="w-4 h-4 flex-shrink-0" />
          <span>Viewing version: <strong>{selectedVersionName}</strong></span>
        </div>
      )}

      {/* Version loading overlay */}
      {versionLoading && (
        <div className="mb-6 flex items-center justify-center py-4">
          <p className="text-gray-500 dark:text-gray-400 text-sm">Loading version...</p>
        </div>
      )}

      {/* Workflows */}
      <section className="mb-8">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-4">
          <GitBranch className="w-5 h-5" />
          Workflows ({workflows.length})
        </h2>
        {workflows.length === 0 ? (
          <p className="text-gray-500 dark:text-gray-400 text-sm">No workflows.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {workflows.map((orch) => (
              <button
                key={orch.orchestration_id}
                onClick={() => handleWorkflowClick(orch.orchestration_id)}
                className="text-left bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <h3 className="font-medium text-gray-900 dark:text-white">{orch.name}</h3>
                {orch.description && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                    {orch.description}
                  </p>
                )}
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                  {orch.step_count} steps
                </p>
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Agents */}
      <section className="mb-8">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-4">
          <Bot className="w-5 h-5" />
          Agents ({agents.length})
        </h2>
        {agents.length === 0 ? (
          <p className="text-gray-500 dark:text-gray-400 text-sm">No agents.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {agents.map((agent) => (
              <div
                key={agent.agent_id}
                className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4"
              >
                <h3 className="font-medium text-gray-900 dark:text-white">{agent.name}</h3>
                {agent.instructions && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                    {agent.instructions}
                  </p>
                )}
                {agent.model && (
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                    Model: {agent.model}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Models */}
      <section className="mb-8">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-4">
          <Database className="w-5 h-5" />
          Models ({models.length})
        </h2>
        {models.length === 0 ? (
          <p className="text-gray-500 dark:text-gray-400 text-sm">No models.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {models.map((model) => (
              <div
                key={model.model_id}
                className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4"
              >
                <h3 className="font-medium text-gray-900 dark:text-white">{model.class_name}</h3>
                {model.description && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                    {model.description}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* More from this creator */}
      {creatorOperations.length > 0 && (
        <section className="mb-8 pt-6 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <User className="w-5 h-5" />
              More from @{project.owner_username}
            </h2>
            <Link
              to={`/users/${project.owner_user_id}`}
              className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
            >
              View profile
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {creatorOperations.map((cp) => (
              <ProjectCard
                key={cp.project_id}
                project={cp}
                onClick={handleCreatorOperationClick}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default SharedProjectViewPage;
